// The company's currency, from the session (set in the platform control
// panel). money() below shows every amount with its symbol.
let currency = 'USD'
let currencyFormat = null
export const setCurrency = (code) => {
  const next = String(code || 'USD').trim().toUpperCase()
  if (next === currency && currencyFormat) return
  try {
    currencyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: next, currencyDisplay: 'narrowSymbol', minimumFractionDigits: 2, maximumFractionDigits: 2 })
    currency = next
  } catch {
    currencyFormat = null
    currency = 'USD'
  }
}
export const currencyCode = () => currency
export const money = (v) => {
  const n = Number(v || 0)
  if (!currencyFormat) setCurrency(currency)
  return currencyFormat ? currencyFormat.format(Number.isNaN(n) ? 0 : n) : `$${fmtMoney(n)}`
}

export const fmtMoney = (v) => {
  const n = Number(v || 0)
  if (Number.isNaN(n)) return '0.00'
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// The company's clock, from the session: the time zone its dates and times
// are recorded in (Settings → Company; the API's company_clock.py). Days here
// follow it too, so a list's "today" is the day the API dates things with,
// wherever the device is. Until it's known, the device's own clock.
let companyZone = null
let zoneParts = null
export const setCompanyZone = (zone) => {
  const next = String(zone || '').trim() || null
  if (next === companyZone) return
  try {
    zoneParts = next ? new Intl.DateTimeFormat('en-US', {
      timeZone: next, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }) : null
    companyZone = next
  } catch {
    zoneParts = null
    companyZone = null
  }
}
export const companyZoneName = () => companyZone

/** Now on the company's clock: a Date whose own year, month, day and hours
    are the company's, for working out days the way the API does. */
export const companyNow = () => {
  const now = new Date()
  if (!zoneParts) return now
  try {
    const p = Object.fromEntries(zoneParts.formatToParts(now).map((x) => [x.type, x.value]))
    return new Date(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second)
  } catch {
    return now
  }
}

// Today as YYYY-MM-DD on the company's clock (the device's until it's
// known). toISOString() gives the UTC day, which is already tomorrow in the
// evening west of Greenwich.
export const localToday = () => {
  const d = companyNow()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// The API sends dates two ways: "2026-10-12 16:12:00" (from str()) and
// "Mon, 12 Oct 2026 16:12:00 GMT" (Flask's JSON for date/datetime columns).
// Both are the shop's own wall-clock time, so read the parts as written
// instead of shifting them into the browser's time zone.
export const parseWallClock = (value) => {
  if (value == null || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const s = String(value).trim()
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s)
  if (m && !/(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
    return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0))
  }
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return null
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds())
}

/** "12 Oct 2026"; pass { year: false } for "12 Oct". */
export const fmtDay = (value, { year = true } = {}) => {
  const d = parseWallClock(value)
  if (!d) return ''
  return d.toLocaleDateString(undefined, year ? { day: 'numeric', month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short' })
}

/** "12 Oct 2026, 4:12 PM" */
export const fmtDateTime = (value) => {
  const d = parseWallClock(value)
  if (!d) return value ? String(value) : ''
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** Midnight today on the company's clock, for "is this date past?" checks
    against parseWallClock. */
export const startOfToday = () => {
  const d = companyNow()
  d.setHours(0, 0, 0, 0)
  return d
}

export const fmtDate = (d) => {
  if (!d) return ''
  try {
    const dt = typeof d === 'string' ? new Date(d) : d
    if (Number.isNaN(dt.getTime())) return String(d)
    return dt.toISOString().slice(0, 10)
  } catch { return String(d) }
}

// Format 'YYYY-MM-DD' safely to 'M/D/YYYY' without timezone shifts.
export const ymdToMDY = (s) => {
  if (!s) return ''
  try {
    const str = String(s).slice(0, 10)
    const [y, m, d] = str.split('-').map((n) => parseInt(n, 10))
    if (!y || !m || !d) return String(s)
    return `${m}/${d}/${y}`
  } catch {
    return String(s)
  }
}
